import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { BookOpen, Plus, Download, Loader2, Trash2, Eye, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API, useAuth, getAuthHeaders } from "../App";
import DashboardHeader from "../components/DashboardHeader";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";

const LectureNotesPage = () => {
  const navigate = useNavigate();
  const { token, refreshUser, user } = useAuth();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteNote, setDeleteNote] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    fetchNotes();
  }, []);

  const fetchNotes = async () => {
    try {
      const response = await axios.get(`${API}/lecture-notes`, {
        headers: getAuthHeaders(token)
      });
      setNotes(response.data);
    } catch (error) {
      toast.error("Failed to load lecture notes");
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (note) => {
    if ((user?.credits || 0) < 2) {
      toast.error("Insufficient credits for download");
      navigate("/pricing");
      return;
    }

    setDownloading(note.notes_id);
    try {
      const response = await axios.post(
        `${API}/lecture-notes/${note.notes_id}/download`,
        {},
        { 
          headers: getAuthHeaders(token),
          responseType: 'blob'
        }
      );
      
      await refreshUser();
      
      // Extract filename from Content-Disposition header or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = `lecture_notes_${note.notes_id}.pdf`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^";\n]+)"?/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1];
        }
      }
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      
      toast.success("Lecture notes PDF downloaded!");
    } catch (error) {
      if (error.response?.status === 402) {
        toast.error("Insufficient credits");
        navigate("/pricing");
      } else {
        toast.error("Failed to download");
      }
    } finally {
      setDownloading(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteNote) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/lecture-notes/${deleteNote.notes_id}`, {
        headers: getAuthHeaders(token)
      });
      toast.success("Lecture notes deleted");
      setNotes(notes.filter(n => n.notes_id !== deleteNote.notes_id));
      setDeleteNote(null);
    } catch (error) {
      toast.error("Failed to delete");
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <DashboardHeader />
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-[#4F46E5]" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="lecture-notes-page">
      <DashboardHeader />

      <main className="container-main py-8">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate("/dashboard")} className="p-2">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                My Lecture Notes
              </h1>
              <p className="text-slate-600">View and download your generated lecture notes</p>
            </div>
          </div>
          <Button 
            onClick={() => navigate("/books")}
            className="btn-primary gap-2"
          >
            <Plus className="w-4 h-4" />
            Create Notes
          </Button>
        </div>

        {notes.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {notes.map((note) => (
              <Card 
                key={note.notes_id}
                className="card-base card-interactive group"
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-100 to-purple-50 rounded-xl flex items-center justify-center">
                      <BookOpen className="w-6 h-6 text-purple-600" />
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
                        {note.key_points?.length || 0} key points
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 text-slate-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteNote(note);
                        }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  <h3 className="font-semibold text-slate-900 mb-2 line-clamp-2">{note.title}</h3>
                  <p className="text-sm text-slate-500 mb-4 line-clamp-2">{note.summary}</p>

                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={() => navigate(`/lecture-notes/${note.notes_id}`)}
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      View
                    </Button>
                    <Button
                      size="sm"
                      className="flex-1 btn-primary"
                      onClick={() => handleDownload(note)}
                      disabled={downloading === note.notes_id}
                    >
                      {downloading === note.notes_id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <Download className="w-4 h-4 mr-1" />
                          Download
                        </>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="card-base">
            <CardContent className="py-16 text-center">
              <div className="w-16 h-16 mx-auto bg-slate-100 rounded-2xl flex items-center justify-center mb-4">
                <BookOpen className="w-8 h-8 text-slate-400" />
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">No lecture notes yet</h3>
              <p className="text-slate-500 mb-6 max-w-md mx-auto">
                Upload a book and generate lecture notes from selected chapters
              </p>
              <Button 
                onClick={() => navigate("/books")}
                className="btn-primary"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Your First Lecture Notes
              </Button>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteNote} onOpenChange={() => setDeleteNote(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Lecture Notes?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteNote?.title}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default LectureNotesPage;
